import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { capitalize } from 'es-toolkit';
import { ArrowRight } from 'lucide-react';
import { useMemo } from 'react';

import { fetchWorkspaceUsage } from '@/entities/workspace/api';
import { WORKSPACE_PLAN } from '@/entities/workspace/constants';
import { UpgradePromotionCard } from '@/features/saved-queries/ui/upgrade-promotion-card';
import { TALK_TO_SALES_LINK } from '@/features/setting/helpers/talk-to-sales';
import { formatLimitValue, formatUsageValue, getUsagePercent } from '../helpers/usage.utils';
import { SettingPageHeader } from '../ui/setting.header';
import { SettingSectionLayout } from '../ui/setting.section';
import type { UsageUpgradeAction } from '../ui/usage.types';
import { WorkspaceLimitRow } from '../ui/workspace-limit-row';
import {
  WorkspaceLimitSectionCard,
  type WorkspaceLimitSection,
} from '../ui/workspace-limit-section-card';

import { Button } from '@/shared/components/shadcn/button';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';

export function UsagePage() {
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const { workspaceId } = useWorkspaceId();
  const { data: workspaceUsage, isFetching: isWorkspaceUsageLoading } = useQuery({
    queryKey: ['workspace-usage', workspaceId],
    queryFn: () => fetchWorkspaceUsage(workspaceId),
  });

  const workspaceLimitSections = useMemo<WorkspaceLimitSection[]>(() => {
    const constraints = workspace?.constraints;
    const socialQueryLimit = constraints?.query_count ?? 200;
    const socialPostLimit = constraints?.post_limit_count ?? 1_000;
    const discoverySearchLimit = constraints?.influencer_search_results_limit ?? 50;
    const discoveryExportLimit = constraints?.influencer_monthly_export_creators_limit ?? 100;
    const discoverySavedListLimit = constraints?.influencer_saved_lists_limit ?? 3;
    const socialQueryUsage = workspaceUsage?.query_count.used ?? 0;
    const discoveryExportUsage = workspaceUsage?.influencer_monthly_export_creators.used ?? 0;
    const discoverySavedListUsage = workspaceUsage?.influencer_saved_lists.used ?? 0;

    return [
      {
        title: 'Social',
        description: 'Usage and capacity for social insight workflows in this workspace.',
        items: [
          {
            label: 'Queries',
            helper: 'Workspace query capacity',
            value: formatUsageValue(socialQueryUsage, socialQueryLimit),
            usage: {
              current: socialQueryUsage,
              limit: socialQueryLimit,
            },
            useProgressBar: false,
          },
          {
            label: 'Posts per query',
            helper: 'Maximum posts retrieved per query',
            value: formatLimitValue(socialPostLimit),
            useProgressBar: false,
          },
        ],
      },
      {
        title: 'Influencer discovery',
        description: 'Limits applied to creator search, exports, and saved discovery workflows.',
        items: [
          {
            label: 'Saved lists',
            helper: 'Lists available in this workspace',
            value: formatUsageValue(discoverySavedListUsage, discoverySavedListLimit),
            usage: {
              current: discoverySavedListUsage,
              limit: discoverySavedListLimit,
            },
            useProgressBar: false,
          },
          {
            label: 'Search results per search',
            helper: 'Maximum creators returned per search',
            value: formatLimitValue(discoverySearchLimit),
            useProgressBar: false,
          },
          {
            label: 'Creator exports',
            helper: 'Monthly creator export capacity. Resets on the 1st of each month.',
            value: formatUsageValue(discoveryExportUsage, discoveryExportLimit),
            usage: {
              current: discoveryExportUsage,
              limit: discoveryExportLimit,
            },
            useProgressBar: true,
          },
        ],
      },
    ];
  }, [workspace?.constraints, workspaceUsage]);

  const upgradeAction = useMemo<UsageUpgradeAction | null>(() => {
    if (workspace.plan === WORKSPACE_PLAN.TRIAL || workspace.plan === WORKSPACE_PLAN.BASIC) {
      return {
        label: 'View plans',
        onClick: () => {
          navigate({
            to: '/workspace/$workspaceId/settings/plan',
            params: { workspaceId },
          });
        },
      };
    }

    return {
      label: 'Talk to sales',
      onClick: () => {
        window.open(TALK_TO_SALES_LINK, '_blank', 'noopener,noreferrer');
      },
    };
  }, [navigate, workspace, workspaceId]);

  const nearLimitItems = useMemo(
    () =>
      workspaceLimitSections.flatMap((section) =>
        section.items
          .filter((item) => {
            if (!item.usage) return false;
            return getUsagePercent(item.usage.current, item.usage.limit) >= 90;
          })
          .map((item) => ({
            sectionTitle: section.title,
            label: item.label,
          })),
      ),
    [workspaceLimitSections],
  );

  const topNudgeDescription = useMemo(() => {
    if (nearLimitItems.length === 0) return null;
    if (nearLimitItems.length === 1) {
      const [item] = nearLimitItems;
      return `${item.sectionTitle} ${item.label.toLowerCase()} is close to its limit. Upgrade now to avoid interruptions.`;
    }

    return `${nearLimitItems.length} usage limits are close to capacity. Upgrade now to keep your team moving without interruptions.`;
  }, [nearLimitItems]);

  return (
    <SettingSectionLayout className="max-w-6xl">
      <SettingPageHeader title="Usage" />
      <section className="flex flex-col gap-4">
        {upgradeAction && topNudgeDescription ? (
          <UpgradePromotionCard
            title="You're getting close to your workspace limits"
            description={topNudgeDescription}
            actions={
              <Button variant="secondary" onClick={upgradeAction.onClick}>
                {upgradeAction.label}
                <ArrowRight className="size-4" />
              </Button>
            }
          />
        ) : null}
        <WorkspaceLimitRow
          item={{
            label: 'Current plan',
            value: workspace ? capitalize(workspace.plan) : 'Plan unavailable',
            helper: 'These limits are applied based on your current plan.',
            useProgressBar: false,
          }}
        />
        <div className="flex flex-col gap-6">
          {workspaceLimitSections.map((section) => (
            <WorkspaceLimitSectionCard
              key={section.title}
              section={section}
              loading={isWorkspaceUsageLoading}
            />
          ))}
        </div>
      </section>
    </SettingSectionLayout>
  );
}
